import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  FiStar,
  FiMessageSquare,
  FiSearch,
  FiX,
  FiAlertCircle,
  FiTrash2,
  FiCheckCircle,
  FiEdit3,
  FiCalendar,
  FiRefreshCw,
  FiUserCheck,
  FiSave,
  FiList,
  FiSend,
  FiCheck,
  FiChevronDown,
  FiLayers,
  FiActivity,
  FiPlus
} from 'react-icons/fi';
import { formatDateTime } from '../utils/dateUtils';

const formatPhoneNumber = (phone) => {
  if (!phone) return '—';
  let cleaned = String(phone).replace(/^91/, '');
  if (cleaned.length === 10) {
    return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }
  return phone;
};

const formatDateTimeIST = (dateStr) => {
  if (!dateStr) return '—';
  const formatted = formatDateTime(dateStr, 'dd MMM yyyy, hh:mm a');
  return formatted || '—';
};

const formatCategoryName = (category) => {
  if (!category) return 'Service';
  const raw = String(category).trim().toLowerCase().replace(/_/g, ' ');
  if (raw === 'front office' || raw === 'front desk') return 'Front Office';
  if (raw === 'it support' || raw === 'it') return 'IT Support';
  if (raw === 'housekeeping' || raw === 'housekeeping service') return 'Housekeeping Service';
  if (raw === 'maintenance' || raw === 'maintenance assistance') return 'Maintenance Assistance';
  if (raw === 'in room dining' || raw === 'ird' || raw === 'dining') return 'In-Room Dining';
  if (raw === 'spa') return 'Spa';
  if (raw === 'yoga') return 'Yoga';
  return raw.replace(/\b\w/g, (c) => c.toUpperCase());
};

const parseResponseContent = (responseText, defaultUser) => {
  if (!responseText) return null;
  try {
    const parsed = JSON.parse(responseText);
    if (typeof parsed === 'object' && parsed !== null) {
      return {
        work_done_by: parsed.work_done_by || defaultUser?.name || 'Staff',
        remarks: parsed.remarks || parsed.note || parsed.response_text || '',
        logged_in_user: parsed.logged_in_user || parsed.logged_in_user_email || defaultUser?.name || defaultUser?.email || 'Staff',
        created_at: parsed.created_at
      };
    }
  } catch (e) {
    // Plain string fallback
  }
  return {
    work_done_by: 'Staff',
    remarks: responseText,
    logged_in_user: defaultUser?.name || defaultUser?.email || 'Admin',
    created_at: null
  };
};

function FeedbackDashboard() {
  const { user: authUser } = useAuth();

  // Feedback State
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterRating, setFilterRating] = useState('all');
  const [activeSection, setActiveSection] = useState('section1');
  const [reviewViewTab, setReviewViewTab] = useState('all'); // 'all' | 'follow_up'

  // Automation & History State
  const [automationSettings, setAutomationSettings] = useState(null);
  const [savingAutomation, setSavingAutomation] = useState(false);
  const [pendingCheckoutSummary, setPendingCheckoutSummary] = useState(null);
  const [loadingPendingCheckout, setLoadingPendingCheckout] = useState(false);
  const [postStayHistory, setPostStayHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Response Form Modal State
  const [responseModalFeedback, setResponseModalFeedback] = useState(null);
  const [responseForm, setResponseForm] = useState({
    work_done_by: '',
    remarks: '',
    update_status: 'resolved',
    is_public: false
  });
  const [submitting, setSubmitting] = useState(false);
  // Stay Context & Requests History State
  const [expandedStayMap, setExpandedStayMap] = useState({});
  const [stayContextData, setStayContextData] = useState({});

  const [message, setMessage] = useState({ type: '', text: '' });

  const fetchFeedback = React.useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      if (filterType !== 'all') {
        params.feedback_type = filterType;
      }
      if (filterRating !== 'all') {
        if (filterRating === 'high') {
          params.min_rating = 4;
        } else if (filterRating === 'low') {
          params.max_rating = 3;
        } else if (filterRating === '5') {
          params.min_rating = 5;
          params.max_rating = 5;
        } else if (filterRating === '4') {
          params.min_rating = 4;
          params.max_rating = 4;
        }
      }
      const response = await api.get('/api/feedback', { params });
      setFeedback(response.data || []);
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
      setMessage({ type: 'error', text: 'Failed to load feedback' });
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, filterRating]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/feedback/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchAutomationSettings = async () => {
    try {
      const response = await api.get('/api/feedback/automation/settings');
      setAutomationSettings(response.data || null);
    } catch (error) {
      console.error('Failed to fetch feedback automation settings:', error);
    }
  };

  const fetchPendingCheckoutSummary = async () => {
    try {
      setLoadingPendingCheckout(true);
      const response = await api.get('/api/feedback/automation/pending-today');
      setPendingCheckoutSummary(response.data || null);
    } catch (error) {
      console.error('Failed to fetch today pending checkout feedback list:', error);
      setPendingCheckoutSummary(null);
    } finally {
      setLoadingPendingCheckout(false);
    }
  };

  const fetchPostStayHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await api.get('/api/feedback/automation/history');
      setPostStayHistory(response.data || []);
    } catch (error) {
      console.error('Failed to fetch post-stay survey history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchFeedback();
    fetchStats();
    fetchAutomationSettings();
    fetchPendingCheckoutSummary();
    fetchPostStayHistory();
  }, [fetchFeedback]);

  const saveAutomationSettings = async () => {
    if (!automationSettings) return;
    try {
      setSavingAutomation(true);
      const payload = {
        daily_checkout_survey_enabled: !!automationSettings.daily_checkout_survey_enabled,
        daily_checkout_survey_time_ist: automationSettings.daily_checkout_survey_time_ist || '18:00'
      };
      const response = await api.put('/api/feedback/automation/settings', payload);
      setAutomationSettings(response.data || automationSettings);
      setMessage({ type: 'success', text: 'Daily send-time settings updated successfully' });
      fetchPendingCheckoutSummary();
      fetchPostStayHistory();
    } catch (error) {
      console.error('Failed to save feedback automation settings:', error);
      setMessage({ type: 'error', text: 'Failed to save feedback send-time settings' });
    } finally {
      setSavingAutomation(false);
    }
  };

  const handleOpenResponseModal = (item) => {
    setResponseModalFeedback(item);
    setResponseForm({
      work_done_by: authUser?.name || '',
      remarks: '',
      update_status: item.status === 'submitted' ? 'resolved' : (item.status || 'resolved'),
      is_public: false
    });
  };

  const handleSubmitResponse = async (e) => {
    e?.preventDefault();
    if (!responseForm.work_done_by.trim()) {
      setMessage({ type: 'error', text: 'Please enter who handled the work (Work Done By)' });
      return;
    }
    if (!responseForm.remarks.trim()) {
      setMessage({ type: 'error', text: 'Please enter remarks / follow-up action taken' });
      return;
    }
    try {
      setSubmitting(true);
      const apiRes = await api.post(`/api/feedback/${responseModalFeedback.id}/responses`, {
        work_done_by: responseForm.work_done_by.trim(),
        remarks: responseForm.remarks.trim(),
        update_status: responseForm.update_status,
        is_public: responseForm.is_public
      });
      setMessage({ type: 'success', text: 'Action remarks saved successfully!' });
      
      const newResponse = apiRes.data;
      const targetId = responseModalFeedback.id;
      const updatedStatus = responseForm.update_status || 'resolved';

      setFeedback((prev) =>
        prev.map((item) => {
          if (item.id === targetId) {
            const existingResps = item.feedback_responses || [];
            return {
              ...item,
              status: updatedStatus,
              follow_up_required: false,
              follow_up_status: 'completed',
              feedback_responses: [
                ...existingResps,
                {
                  id: newResponse.id,
                  response_text: newResponse.response_text,
                  is_public: newResponse.is_public,
                  created_at: newResponse.created_at,
                  dashboard_users: {
                    name: authUser?.name || 'Staff',
                    email: authUser?.email || ''
                  }
                }
              ]
            };
          }
          return item;
        })
      );

      setResponseModalFeedback(null);
      setResponseForm({ work_done_by: '', remarks: '', update_status: 'resolved', is_public: false });
      fetchStats();
    } catch (error) {
      console.error('Failed to save response remarks:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save action remarks' });
    } finally {
      setSubmitting(false);
    }
  };

  const fetchStayContext = async (feedbackId) => {
    try {
      setStayContextData((prev) => ({
        ...prev,
        [feedbackId]: { ...(prev[feedbackId] || {}), loading: true }
      }));
      const res = await api.get(`/api/feedback/${feedbackId}/guest-context`);
      setStayContextData((prev) => ({
        ...prev,
        [feedbackId]: {
          guest: res.data?.guest,
          service_requests: res.data?.service_requests || [],
          loading: false
        }
      }));
    } catch (err) {
      console.error('Failed to load guest stay context:', err);
      setStayContextData((prev) => ({
        ...prev,
        [feedbackId]: {
          guest: null,
          service_requests: [],
          loading: false,
          error: 'Failed to load details'
        }
      }));
    }
  };

  const toggleStayContext = (feedbackId) => {
    setExpandedStayMap((prev) => {
      const nextState = !prev[feedbackId];
      if (nextState && !stayContextData[feedbackId]?.guest && !stayContextData[feedbackId]?.service_requests) {
        fetchStayContext(feedbackId);
      }
      return { ...prev, [feedbackId]: nextState };
    });
  };

  const handleDeleteFeedback = async (id) => {
    if (authUser?.role !== 'admin') {
      setMessage({ type: 'error', text: 'Only admin users are permitted to delete feedback records.' });
      return;
    }
    if (!window.confirm('Are you sure you want to delete this feedback record?')) {
      return;
    }
    try {
      await api.delete(`/api/feedback/${id}`);
      setMessage({ type: 'success', text: 'Feedback deleted successfully' });
      fetchFeedback();
      fetchStats();
    } catch (error) {
      console.error('Failed to delete feedback:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to delete feedback' });
    }
  };

  const renderStars = (rating) => {
    if (!rating) return null;
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <FiStar
            key={star}
            className={`${star <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
            size={14}
          />
        ))}
        <span className="ml-1 text-xs font-bold text-gray-800">
          {rating}/5
        </span>
      </div>
    );
  };

  const filteredFeedback = feedback.filter((item) => {
    // 1. Follow-up required view tab filter: only include reviews needing follow-up that are NOT yet acknowledged or resolved
    if (reviewViewTab === 'follow_up') {
      const isPendingFollowUp = (item.follow_up_required === true || (item.overall_rating && item.overall_rating <= 3)) &&
        item.status !== 'acknowledged' && item.status !== 'resolved';
      if (!isPendingFollowUp) return false;
    }

    // 2. Acknowledged view tab filter: reviews that have been acknowledged, resolved, or had action remarks logged
    if (reviewViewTab === 'acknowledged') {
      const isAcknowledged = item.status === 'acknowledged' || item.status === 'resolved' || (item.feedback_responses && item.feedback_responses.length > 0);
      if (!isAcknowledged) return false;
    }

    // 3. Search query filter
    const query = searchQuery.toLowerCase();
    const guestName = item.guests?.name?.toLowerCase() || '';
    const comment = item.comment?.toLowerCase() || '';
    const room = String(item.room_number || item.guests?.room_no || '').toLowerCase();
    const phone = String(item.guests?.phone || '').toLowerCase();
    return !searchQuery || guestName.includes(query) || comment.includes(query) || room.includes(query) || phone.includes(query);
  });

  return (
    <Layout>
      <div className="space-y-5 px-2 py-3 sm:px-3 sm:py-4 lg:px-1 lg:py-2">
        {/* Header Title */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Guest Feedback &amp; Reviews</h1>
            <p className="text-sm text-gray-500 mt-0.5">Manage guest ratings, follow-up actions, and automated checkout survey dispatch</p>
          </div>
          <button
            onClick={() => {
              fetchFeedback();
              fetchStats();
              fetchPendingCheckoutSummary();
              fetchPostStayHistory();
            }}
            className="self-start sm:self-auto bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-xs transition"
          >
            <FiRefreshCw size={13} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Global Alert Notification */}
        {message.text && (
          <div
            className={`p-3.5 rounded-lg border flex items-center justify-between text-xs font-medium ${
              message.type === 'success'
                ? 'bg-green-50 text-green-800 border-green-200'
                : 'bg-red-50 text-red-800 border-red-200'
            }`}
          >
            <div className="flex items-center gap-2">
              {message.type === 'success' ? <FiCheckCircle className="text-green-600" size={16} /> : <FiAlertCircle className="text-red-600" size={16} />}
              <span>{message.text}</span>
            </div>
            <button onClick={() => setMessage({ type: '', text: '' })} className="text-gray-400 hover:text-gray-600">
              <FiX size={15} />
            </button>
          </div>
        )}

        {/* Top KPI Metrics Cards */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              onClick={() => {
                setActiveSection('section1');
                setReviewViewTab('all');
              }}
              className={`text-left bg-white rounded-xl border p-4 shadow-xs transition cursor-pointer ${
                activeSection === 'section1' && reviewViewTab === 'all'
                  ? 'border-gray-900 ring-2 ring-gray-900/10'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Total Reviews</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{stats.total || 0}</p>
            </button>
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-xs">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Average Rating</p>
              <p className="mt-2 text-2xl font-bold text-gray-900 flex items-center gap-1.5">
                {stats.average_overall > 0 ? parseFloat(stats.average_overall).toFixed(1) : 'N/A'}
                <span className="text-amber-500 text-lg leading-none">★</span>
              </p>
            </div>
            <button
              onClick={() => {
                setActiveSection('section1');
                setReviewViewTab('follow_up');
              }}
              className={`text-left bg-white rounded-xl border p-4 shadow-xs transition cursor-pointer ${
                activeSection === 'section1' && reviewViewTab === 'follow_up'
                  ? 'border-rose-500 ring-2 ring-rose-500/20 bg-rose-50/20'
                  : 'border-gray-200 hover:border-rose-300'
              }`}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-rose-600 flex items-center gap-1.5">
                <FiAlertCircle size={12} />
                <span>Follow-up Required</span>
              </p>
              <p className="mt-2 text-2xl font-bold text-rose-600">{stats.follow_up_required || 0}</p>
            </button>
          </div>
        )}

        {/* Software Themed Section Switcher Tabs */}
        <div className="overflow-x-auto pb-1">
          <div className="flex items-center gap-1.5 min-w-max rounded-xl bg-gray-50 border border-gray-200 p-1.5">
            <button
              onClick={() => setActiveSection('section1')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 whitespace-nowrap ${
                activeSection === 'section1'
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-200 font-bold'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/50'
              }`}
            >
              <FiMessageSquare className={activeSection === 'section1' ? 'text-indigo-600' : 'text-gray-400'} size={15} />
              <span>Section 1: Guest Reviews &amp; Follow-up Log</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                activeSection === 'section1' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-gray-200 text-gray-600'
              }`}>
                {feedback.length}
              </span>
            </button>

            <button
              onClick={() => setActiveSection('section2')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 whitespace-nowrap ${
                activeSection === 'section2'
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-200 font-bold'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/50'
              }`}
            >
              <FiSend className={activeSection === 'section2' ? 'text-emerald-600' : 'text-gray-400'} size={15} />
              <span>Section 2: Checkout Automation &amp; Message History</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                activeSection === 'section2' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-200 text-gray-600'
              }`}>
                {pendingCheckoutSummary?.total_checkout_guests || 0} Today
              </span>
            </button>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* SECTION 1: GUEST REVIEWS & ACTION / FOLLOW-UP REMARKS                     */}
        {/* ========================================================================= */}
        {activeSection === 'section1' && (
          <div className="space-y-4">
            {/* View Filter Switcher: All Reviews vs Follow-up Required vs Acknowledged */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setReviewViewTab('all')}
                className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold flex items-center gap-2 transition cursor-pointer ${
                  reviewViewTab === 'all'
                    ? 'bg-gray-900 text-white shadow-xs'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <FiMessageSquare size={14} className={reviewViewTab === 'all' ? 'text-white' : 'text-gray-500'} />
                <span>All Reviews</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${
                  reviewViewTab === 'all' ? 'bg-gray-800 text-gray-200' : 'bg-gray-100 text-gray-600'
                }`}>
                  {feedback.length}
                </span>
              </button>

              <button
                onClick={() => setReviewViewTab('follow_up')}
                className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold flex items-center gap-2 transition cursor-pointer ${
                  reviewViewTab === 'follow_up'
                    ? 'bg-rose-600 text-white shadow-xs'
                    : 'bg-white text-rose-700 border border-rose-200 hover:bg-rose-50'
                }`}
              >
                <FiAlertCircle size={14} className={reviewViewTab === 'follow_up' ? 'text-white' : 'text-rose-600'} />
                <span>Follow-up Required</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${
                  reviewViewTab === 'follow_up' ? 'bg-rose-700 text-white' : 'bg-rose-100 text-rose-700'
                }`}>
                  {feedback.filter(item => (item.follow_up_required || (item.overall_rating && item.overall_rating <= 3)) && item.status !== 'acknowledged' && item.status !== 'resolved').length}
                </span>
              </button>

              <button
                onClick={() => setReviewViewTab('acknowledged')}
                className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold flex items-center gap-2 transition cursor-pointer ${
                  reviewViewTab === 'acknowledged'
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'bg-white text-emerald-700 border border-emerald-200 hover:bg-emerald-50'
                }`}
              >
                <FiCheckCircle size={14} className={reviewViewTab === 'acknowledged' ? 'text-white' : 'text-emerald-600'} />
                <span>Acknowledged</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${
                  reviewViewTab === 'acknowledged' ? 'bg-emerald-700 text-white' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {feedback.filter(item => item.status === 'acknowledged' || item.status === 'resolved' || (item.feedback_responses && item.feedback_responses.length > 0)).length}
                </span>
              </button>
            </div>
            {/* Clean Filter Toolbar */}
            <div className="bg-white rounded-xl border border-gray-200 p-3 shadow-xs flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
              <div className="flex-1 relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                <input
                  type="text"
                  placeholder="Search guest name, room, phone, or comment..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 bg-gray-50/60 border border-gray-200 rounded-lg text-xs sm:text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:bg-white focus:border-gray-400 transition"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={filterRating}
                  onChange={(e) => setFilterRating(e.target.value)}
                  className="bg-gray-50/60 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 px-2.5 py-1.5 focus:outline-none focus:bg-white focus:border-gray-400"
                >
                  <option value="all">All Ratings</option>
                  <option value="5">★★★★★ 5 Stars</option>
                  <option value="4">★★★★ 4 Stars</option>
                  <option value="low">1-3 Stars (Low Ratings)</option>
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="bg-gray-50/60 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 px-2.5 py-1.5 focus:outline-none focus:bg-white focus:border-gray-400"
                >
                  <option value="all">All Status</option>
                  <option value="submitted">Submitted</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="resolved">Resolved</option>
                  <option value="archived">Archived</option>
                </select>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="bg-gray-50/60 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 px-2.5 py-1.5 focus:outline-none focus:bg-white focus:border-gray-400"
                >
                  <option value="all">All Types</option>
                  <option value="post_stay">Post-Stay</option>
                  <option value="real_time">Real-Time</option>
                  <option value="service">Service</option>
                  <option value="general">General</option>
                </select>
              </div>
            </div>

            {/* Clean Review Feed */}
            {loading ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-gray-500 shadow-xs">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-2"></div>
                <p className="text-xs font-medium">Loading reviews...</p>
              </div>
            ) : filteredFeedback.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-gray-500 shadow-xs">
                <FiMessageSquare className="mx-auto mb-2 text-gray-300" size={36} />
                <p className="text-sm font-semibold text-gray-700">No feedback reviews found</p>
                <p className="text-xs text-gray-400 mt-0.5">Try adjusting your filters or search terms</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredFeedback.map((item) => {
                  const responses = item.feedback_responses || [];
                  const isLowRating = item.overall_rating && item.overall_rating <= 3;
                  const room = item.room_number || item.guests?.room_no;
                  const phone = item.guests?.phone;
                  const checkin = item.guests?.checkin;
                  const checkout = item.guests?.checkout;
                  const qrGuest = item.guests?.qr_guest;
                  const qrScannedAt = item.guests?.last_active_at;
                  const isStayExpanded = !!expandedStayMap[item.id];
                  const contextInfo = stayContextData[item.id];
                  const reqCount = contextInfo?.service_requests?.length ?? item.service_requests_count ?? 0;

                  return (
                    <div
                      key={item.id}
                      className="bg-white rounded-xl border border-gray-200/80 shadow-2xs hover:border-gray-300 transition-all duration-150 overflow-hidden"
                    >
                      {/* Card Main Area */}
                      <div className="p-4 sm:p-5">
                        {/* Header: Name, clean metadata, and status */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-1">
                          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs">
                            <span className="font-bold text-gray-900 text-sm sm:text-base tracking-tight">
                              {item.guests?.name || 'Guest'}
                            </span>
                            {room && (
                              <span className="font-semibold text-gray-700">
                                Room {room}
                              </span>
                            )}
                            {phone && (
                              <>
                                <span className="text-gray-300">•</span>
                                <span className="text-gray-500 font-normal">
                                  {formatPhoneNumber(phone)}
                                </span>
                              </>
                            )}
                            {checkin ? (
                              <>
                                <span className="text-gray-300">•</span>
                                <span className="text-gray-500 font-normal flex items-center gap-1">
                                  <FiCalendar size={11} className="text-gray-400" />
                                  {checkout
                                    ? `${formatDateTime(checkin, 'dd MMM')} – ${formatDateTime(checkout, 'dd MMM yyyy')}`
                                    : `Check-in: ${formatDateTime(checkin, 'dd MMM yyyy')}`}
                                </span>
                                {qrGuest && (
                                  <span
                                    className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded"
                                    title={`Room QR scanned${qrScannedAt ? ` on ${formatDateTime(qrScannedAt, 'dd MMM yyyy, hh:mm a')}` : ''}`}
                                  >
                                    📱 QR
                                  </span>
                                )}
                              </>
                            ) : (qrScannedAt || qrGuest) ? (
                              <>
                                <span className="text-gray-300">•</span>
                                <span className="text-blue-700 font-medium flex items-center gap-1 text-[11px]" title="Room QR code scan timestamp">
                                  <span>📱 QR Scanned at:</span>
                                  <span>{qrScannedAt ? formatDateTime(qrScannedAt, 'dd MMM yyyy, hh:mm a') : formatDateTime(item.created_at, 'dd MMM yyyy')}</span>
                                </span>
                              </>
                            ) : null}
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            {item.follow_up_required && item.status !== 'acknowledged' && item.status !== 'resolved' && (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200/70">
                                <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                                Follow-up Needed
                              </span>
                            )}
                            {(item.status === 'acknowledged' || item.status === 'resolved' || responses.length > 0) ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/70">
                                <FiCheck size={11} className="text-emerald-600" />
                                {item.status === 'resolved' ? 'Resolved' : 'Acknowledged'}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium text-gray-500 bg-gray-50 border border-gray-200/60 capitalize">
                                {item.status}
                              </span>
                            )}
                            <span className="text-xs text-gray-400 font-normal pl-1">
                              {formatDateTimeIST(item.created_at)}
                            </span>
                          </div>
                        </div>

                        {/* Rating, Category & Actions */}
                        <div className="mt-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            {renderStars(item.overall_rating)}
                            <span className="text-gray-300">•</span>
                            <span className="text-xs font-medium text-gray-500 capitalize">
                              {item.feedback_type?.replace('_', ' ')}
                            </span>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            {/* Requests Toggle Button */}
                            <button
                              type="button"
                              onClick={() => toggleStayContext(item.id)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer border ${
                                isStayExpanded
                                  ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                  : 'bg-gray-50/80 hover:bg-gray-100 text-gray-700 border-gray-200/80'
                              }`}
                            >
                              <FiLayers size={13} className={isStayExpanded ? 'text-indigo-600' : 'text-gray-500'} />
                              <span>{isStayExpanded ? `Hide Requests (${reqCount})` : `View Requests (${reqCount})`}</span>
                              <FiChevronDown size={13} className={`transition-transform duration-200 ${isStayExpanded ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Action Remarks / Review Buttons */}
                            {responses.length > 0 ? (
                              <div className="flex items-center gap-1.5">
                                <span className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                                  <FiCheck size={12} className="text-emerald-600" />
                                  <span>{responses.length} {responses.length === 1 ? 'Review' : 'Reviews'} Logged</span>
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleOpenResponseModal(item)}
                                  className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 flex items-center gap-1 transition cursor-pointer"
                                  title="Add another review / action remark"
                                >
                                  <FiPlus size={12} />
                                  <span>Add Review</span>
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleOpenResponseModal(item)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer shadow-2xs ${
                                  isLowRating
                                    ? 'bg-rose-600 hover:bg-rose-700 text-white'
                                    : 'bg-gray-900 hover:bg-gray-800 text-white'
                                }`}
                              >
                                <FiEdit3 size={12} />
                                <span>Log Remarks</span>
                              </button>
                            )}

                            {authUser?.role === 'admin' && (
                              <button
                                onClick={() => handleDeleteFeedback(item.id)}
                                className="p-1.5 text-gray-300 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition ml-0.5"
                                title="Delete Feedback"
                              >
                                <FiTrash2 size={14} />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Guest Written Comment */}
                        {item.comment && item.comment.trim() && (
                          <div className="mt-3 text-xs sm:text-sm text-gray-800 bg-gray-50/70 p-3 rounded-xl border border-gray-100 leading-relaxed font-normal">
                            &ldquo;{item.comment}&rdquo;
                          </div>
                        )}
                      </div>

                      {/* Expandable Stay & Service Requests Drawer */}
                      {isStayExpanded && (
                        <div className="bg-slate-50/70 border-t border-gray-100 p-4 sm:p-5 space-y-3 transition-all">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 border-b border-gray-200/60">
                            <div>
                              <h4 className="text-xs font-bold text-gray-900 flex items-center gap-1.5">
                                <FiActivity size={13} className="text-indigo-600" />
                                <span>Stay Timeline &amp; WhatsApp Bot Requests</span>
                              </h4>
                              <p className="text-[11px] text-gray-500">Service requests placed by this guest through WhatsApp during this stay</p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 text-xs text-gray-600">
                              {checkin ? (
                                <>
                                  <span>Check-in: <strong className="text-gray-900">{formatDateTime(checkin, 'dd MMM yyyy')}</strong></span>
                                  {checkout && (
                                    <>
                                      <span className="text-gray-300">•</span>
                                      <span>Check-out: <strong className="text-gray-900">{formatDateTime(checkout, 'dd MMM yyyy')}</strong></span>
                                    </>
                                  )}
                                  {qrScannedAt && (
                                    <>
                                      <span className="text-gray-300">•</span>
                                      <span className="text-blue-600">QR Scanned: <strong className="text-blue-700">{formatDateTime(qrScannedAt, 'dd MMM yyyy, hh:mm a')}</strong></span>
                                    </>
                                  )}
                                </>
                              ) : (qrScannedAt || qrGuest) ? (
                                <span className="text-blue-700 font-medium flex items-center gap-1.5">
                                  <span>📱 QR Scanned at:</span>
                                  <strong className="text-blue-900">
                                    {qrScannedAt ? formatDateTime(qrScannedAt, 'dd MMM yyyy, hh:mm a') : formatDateTime(item.created_at, 'dd MMM yyyy')}
                                  </strong>
                                </span>
                              ) : (
                                <span>Stay Dates: <strong className="text-gray-400">—</strong></span>
                              )}
                            </div>
                          </div>

                          {contextInfo?.loading ? (
                            <div className="py-6 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                              <span>Loading guest service requests...</span>
                            </div>
                          ) : !contextInfo?.service_requests || contextInfo.service_requests.length === 0 ? (
                            <div className="bg-white p-4 rounded-xl border border-gray-200/70 text-center py-4 space-y-0.5">
                              <p className="text-xs font-semibold text-gray-700">No Service Requests Logged</p>
                              <p className="text-[11px] text-gray-400">This guest did not place any service requests via the WhatsApp bot during this stay.</p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-xs font-semibold text-gray-600">
                                <span>Requests ({contextInfo.service_requests.length})</span>
                                <span className="text-[11px] text-gray-400 font-normal">Newest first</span>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                                {contextInfo.service_requests.map((req) => {
                                  const reqStatus = req.status || 'received';
                                  const isReqCompleted = reqStatus === 'completed';
                                  const isReqDispute = req.dispatch_status === 'guest_disputed' || req.dispatch_status === 'critical_escalated';
                                  const isReqEscalated = req.dispatch_status === 'escalated' || req.dispatch_status === 'super_escalated';
                                  const workDoneBy = req.work_done_by || req.metadata?.flow_work_done_by || req.metadata?.completed_by_staff_name || req.metadata?.dashboard_completed_by || req.metadata?.staff_acknowledged_by_name || '';
                                  const remarks = req.remarks || req.metadata?.flow_remarks || req.metadata?.completion_remarks || req.metadata?.remarks || req.metadata?.staff_completion_remarks || '';
                                  const completedAt = req.completed_at || req.metadata?.completed_by_staff_at || req.metadata?.completion_response_at || req.metadata?.dashboard_completion_at || '';

                                  return (
                                    <div key={req.id} className="bg-white p-3.5 rounded-xl border border-gray-200/90 shadow-2xs space-y-2 hover:border-gray-300 transition">
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="text-xs font-bold text-gray-900">
                                          {formatCategoryName(req.category)}
                                        </span>
                                        <span
                                          className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${
                                            isReqDispute
                                              ? 'bg-rose-50 text-rose-700 border-rose-200'
                                              : isReqEscalated
                                                ? 'bg-purple-50 text-purple-700 border-purple-200'
                                                : isReqCompleted
                                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                  : 'bg-amber-50 text-amber-700 border-amber-200'
                                          }`}
                                        >
                                          {isReqDispute
                                            ? 'Dispute'
                                            : isReqEscalated
                                              ? 'Escalated'
                                              : reqStatus.toUpperCase()}
                                        </span>
                                      </div>
                                      
                                      <p className="text-xs text-gray-700 font-medium leading-snug">
                                        {req.details || req.metadata?.requested_activity || 'No details provided'}
                                      </p>

                                      {/* Work Done By & Completion Info */}
                                      {(workDoneBy || remarks || completedAt) && (
                                        <div className="bg-slate-50/90 p-2.5 rounded-lg border border-slate-100 space-y-1 text-[11px]">
                                          {workDoneBy && (
                                            <div className="flex items-center gap-1.5 text-gray-600">
                                              <span className="font-medium text-gray-500">Work Done By:</span>
                                              <span className="text-gray-900 font-semibold">{workDoneBy}</span>
                                            </div>
                                          )}
                                          {completedAt && (
                                            <div className="flex items-center gap-1.5 text-gray-600">
                                              <span className="font-medium text-gray-500">Completed:</span>
                                              <span className="text-emerald-700 font-medium">{formatDateTimeIST(completedAt)}</span>
                                            </div>
                                          )}
                                          {remarks && (
                                            <div className="text-gray-700 pt-0.5 border-t border-slate-200/50">
                                              <span className="font-medium text-gray-500">Remarks: </span>
                                              <span className="text-gray-800 italic">{remarks}</span>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      <div className="flex items-center justify-between text-[10px] text-gray-400 pt-1 border-t border-gray-100">
                                        <span>Room {req.room_no || room || '—'}</span>
                                        <span>Requested: {formatDateTimeIST(req.created_at)}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Saved Work Done & Action Remarks Box */}
                      {responses.length > 0 && (
                        <div className="bg-gray-50/70 border-t border-gray-100 p-3.5 sm:px-5 sm:py-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                              <FiUserCheck size={13} className="text-indigo-600" />
                              <span>Action Taken &amp; Resolution Remarks ({responses.length})</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleOpenResponseModal(item)}
                              className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 cursor-pointer transition"
                              title="Add another review or action remark to this feedback"
                            >
                              <FiPlus size={12} />
                              <span>Add Another Review</span>
                            </button>
                          </div>
                          {responses.map((resp, respIdx) => {
                            const parsed = parseResponseContent(resp.response_text, resp.dashboard_users);
                            return (
                              <div key={resp.id || respIdx} className="bg-white p-3 rounded-xl border border-gray-200/80 text-xs space-y-1 shadow-2xs">
                                <div className="flex flex-wrap items-center justify-between gap-1 text-gray-500 pb-1 border-b border-gray-100">
                                  <div className="flex items-center gap-2">
                                    {responses.length > 1 && (
                                      <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
                                        #{respIdx + 1}
                                      </span>
                                    )}
                                    <div>
                                      <span className="font-semibold text-gray-700">Work Done By: </span>
                                      <span className="text-indigo-600 font-bold">{parsed?.work_done_by || 'Staff'}</span>
                                    </div>
                                  </div>
                                  <div className="text-[11px] text-gray-400">
                                    Logged by <span className="font-medium text-gray-600">{parsed?.logged_in_user || resp.dashboard_users?.name || 'Admin'}</span> • {formatDateTimeIST(resp.created_at)}
                                  </div>
                                </div>
                                <p className="text-xs sm:text-sm text-gray-800 font-medium whitespace-pre-wrap pt-0.5">
                                  {parsed?.remarks || resp.response_text}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ========================================================================= */}
        {/* SECTION 2: CHECKOUT AUTOMATION & POST-STAY MESSAGE LOGS (TOGETHER)        */}
        {/* ========================================================================= */}
        {activeSection === 'section2' && (
          <div className="space-y-4">
            {/* Automation Configuration Bar */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-xs">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <div className="space-y-1">
                  <div className="text-sm font-bold text-gray-900">Daily Checkout Feedback Automation (IST)</div>
                  <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
                    <label className="flex items-center gap-2 cursor-pointer font-medium">
                      <input
                        type="checkbox"
                        checked={!!automationSettings?.daily_checkout_survey_enabled}
                        onChange={(e) =>
                          setAutomationSettings((prev) => ({
                            ...(prev || {}),
                            daily_checkout_survey_enabled: e.target.checked
                          }))
                        }
                        className="rounded text-gray-900 focus:ring-gray-900"
                      />
                      Enable daily checkout feedback send
                    </label>
                    <label className="flex items-center gap-2 font-medium">
                      Send Time (IST):
                      <input
                        type="time"
                        value={automationSettings?.daily_checkout_survey_time_ist || '18:00'}
                        onChange={(e) =>
                          setAutomationSettings((prev) => ({
                            ...(prev || {}),
                            daily_checkout_survey_time_ist: e.target.value
                          }))
                        }
                        className="px-2 py-1 bg-white border border-gray-300 rounded text-xs focus:outline-none focus:border-gray-500"
                      />
                    </label>
                  </div>
                </div>

                <button
                  onClick={saveAutomationSettings}
                  disabled={savingAutomation}
                  className="self-start lg:self-auto bg-gray-900 hover:bg-gray-800 text-white px-3.5 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50 transition shadow-xs cursor-pointer"
                >
                  <FiSave size={13} />
                  <span>{savingAutomation ? 'Saving...' : 'Save Configuration'}</span>
                </button>
              </div>

              {/* Stat Counters Row */}
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-3 border-t border-gray-100">
                <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                  <div className="text-[11px] font-medium text-gray-500">Today&apos;s Checkouts</div>
                  <div className="text-lg font-bold text-gray-900">{pendingCheckoutSummary?.total_checkout_guests || 0}</div>
                </div>
                <div className="bg-amber-50/60 p-2.5 rounded-lg border border-amber-100">
                  <div className="text-[11px] font-medium text-amber-700">Pending at {automationSettings?.daily_checkout_survey_time_ist || '18:00'} IST</div>
                  <div className="text-lg font-bold text-amber-700">{pendingCheckoutSummary?.pending_count || 0}</div>
                </div>
                <div className="bg-emerald-50/60 p-2.5 rounded-lg border border-emerald-100">
                  <div className="text-[11px] font-medium text-emerald-700">Sent Today</div>
                  <div className="text-lg font-bold text-emerald-700">{pendingCheckoutSummary?.sent_count || 0}</div>
                </div>
                <div className="bg-rose-50/60 p-2.5 rounded-lg border border-rose-100">
                  <div className="text-[11px] font-medium text-rose-700">Failed Today</div>
                  <div className="text-lg font-bold text-rose-700">{pendingCheckoutSummary?.failed_count || 0}</div>
                </div>
              </div>
            </div>

            {/* Table 1: Today's Checkout List */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50/80 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FiCalendar className="text-indigo-600" size={15} />
                  <span className="text-xs font-bold text-gray-800 uppercase tracking-wide">Today&apos;s Checkout Scheduled List</span>
                  <span className="px-2 py-0.5 text-xs font-semibold bg-gray-200 text-gray-700 rounded">
                    {pendingCheckoutSummary?.guests?.length || 0} guests
                  </span>
                </div>
                <span className="text-xs text-gray-500 font-medium">Scheduled for {automationSettings?.daily_checkout_survey_time_ist || '18:00'} IST</span>
              </div>

              {loadingPendingCheckout ? (
                <div className="p-8 text-center text-xs text-gray-500">Loading today&apos;s checkout list...</div>
              ) : (pendingCheckoutSummary?.guests || []).length === 0 ? (
                <div className="p-8 text-center text-xs text-gray-500 italic">No guests are checking out today.</div>
              ) : (
                <div className="overflow-x-auto max-h-72">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50 text-gray-600 font-semibold tracking-wider uppercase text-[11px]">
                        <th className="py-2.5 px-3.5">Guest</th>
                        <th className="py-2.5 px-3.5">Room</th>
                        <th className="py-2.5 px-3.5">Phone</th>
                        <th className="py-2.5 px-3.5">Dispatch Status</th>
                        <th className="py-2.5 px-3.5">Attempt Time (IST)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {(pendingCheckoutSummary?.guests || []).map((guest) => (
                        <tr key={guest.guest_id} className="hover:bg-gray-50/80 transition-colors">
                          <td className="py-2.5 px-3.5 font-semibold text-gray-900">{guest.name || 'Guest'}</td>
                          <td className="py-2.5 px-3.5 text-gray-700">{guest.room_no || '—'}</td>
                          <td className="py-2.5 px-3.5 text-gray-600">{formatPhoneNumber(guest.phone)}</td>
                          <td className="py-2.5 px-3.5">
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-semibold border ${
                                guest.status === 'sent'
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  : guest.status === 'failed'
                                    ? 'bg-rose-50 text-rose-700 border-rose-200'
                                    : guest.status === 'skipped'
                                      ? 'bg-gray-100 text-gray-700 border-gray-200'
                                      : 'bg-amber-50 text-amber-700 border-amber-200'
                              }`}
                            >
                              {guest.status === 'sent'
                                ? 'Sent (1 Attempt)'
                                : guest.status === 'failed'
                                  ? 'Failed (1 Attempt)'
                                  : guest.status === 'skipped'
                                    ? 'Skipped (Past Cutoff)'
                                    : 'Pending'}
                            </span>
                          </td>
                          <td className="py-2.5 px-3.5 text-gray-600">
                            {guest.status === 'sent' || guest.status === 'failed' ? (
                              <div>
                                <span className="font-semibold text-gray-900">{formatDateTimeIST(guest.sent_at)}</span>
                                {guest.error_message && (
                                  <p className="text-rose-600 text-[11px] mt-0.5 truncate max-w-xs" title={guest.error_message}>
                                    {guest.error_message}
                                  </p>
                                )}
                              </div>
                            ) : guest.status === 'skipped' ? (
                              <span className="text-gray-500 italic">{guest.error_message || 'Added after cutoff'}</span>
                            ) : (
                              <span className="text-amber-700 italic">Will send at {automationSettings?.daily_checkout_survey_time_ist || '18:00'} IST</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Table 2: Post-Stay Message History */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50/80 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FiList className="text-emerald-600" size={15} />
                  <span className="text-xs font-bold text-gray-800 uppercase tracking-wide">Post-Stay Message History Log</span>
                  <span className="px-2 py-0.5 text-xs font-semibold bg-gray-200 text-gray-700 rounded">
                    {postStayHistory.length} records
                  </span>
                </div>
                <span className="text-xs text-gray-500 font-medium">All historical dispatches</span>
              </div>

              {loadingHistory ? (
                <div className="p-8 text-center text-xs text-gray-500">Loading history log...</div>
              ) : postStayHistory.length === 0 ? (
                <div className="p-8 text-center text-xs text-gray-500 italic">No post-stay message history recorded yet.</div>
              ) : (
                <div className="overflow-x-auto max-h-80">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50 text-gray-600 font-semibold tracking-wider uppercase text-[11px]">
                        <th className="py-2.5 px-3.5">Guest</th>
                        <th className="py-2.5 px-3.5">Room</th>
                        <th className="py-2.5 px-3.5">Phone</th>
                        <th className="py-2.5 px-3.5">Checkout Date</th>
                        <th className="py-2.5 px-3.5">Status</th>
                        <th className="py-2.5 px-3.5">Attempt Time (IST)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {postStayHistory.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50/80 transition-colors">
                          <td className="py-2.5 px-3.5 font-semibold text-gray-900">{item.guest_name}</td>
                          <td className="py-2.5 px-3.5 text-gray-700">{item.room_no || '—'}</td>
                          <td className="py-2.5 px-3.5 text-gray-600">{formatPhoneNumber(item.guest_phone)}</td>
                          <td className="py-2.5 px-3.5 text-gray-700">{item.checkout_date || '—'}</td>
                          <td className="py-2.5 px-3.5">
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-semibold border ${
                                item.status === 'sent'
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  : item.status === 'failed'
                                    ? 'bg-rose-50 text-rose-700 border-rose-200'
                                    : 'bg-amber-50 text-amber-700 border-amber-200'
                              }`}
                            >
                              {item.status === 'sent' ? 'Sent' : item.status === 'failed' ? 'Failed' : item.status}
                            </span>
                          </td>
                          <td className="py-2.5 px-3.5 text-gray-600">
                            <span className="font-semibold text-gray-900">{formatDateTimeIST(item.sent_at)}</span>
                            {item.error_message && (
                              <p className="text-rose-600 text-[11px] mt-0.5 truncate max-w-xs" title={item.error_message}>
                                {item.error_message}
                              </p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* ACTION / FOLLOW-UP REMARKS MODAL                                          */}
        {/* ========================================================================= */}
        {responseModalFeedback && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-lg w-full shadow-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/80">
                <div>
                  <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2">
                    <FiEdit3 className="text-indigo-600" />
                    {responseModalFeedback.feedback_responses?.length > 0
                      ? `Add Review / Action Remark (Review #${responseModalFeedback.feedback_responses.length + 1})`
                      : 'Log Work Done & Action Remarks'}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {responseModalFeedback.feedback_responses?.length > 0
                      ? 'Add another staff follow-up note and update resolution status'
                      : 'Save staff follow-up notes and update resolution status'}
                  </p>
                </div>
                <button
                  onClick={() => setResponseModalFeedback(null)}
                  className="text-gray-400 hover:text-gray-600 transition cursor-pointer"
                >
                  <FiX size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmitResponse} className="p-5 space-y-4">
                {/* Guest Context Preview */}
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs space-y-1">
                  <div className="flex justify-between items-center font-bold text-gray-900">
                    <span>{responseModalFeedback.guests?.name || 'Guest'}</span>
                    <span className="text-gray-600 font-normal">Room {responseModalFeedback.room_number || responseModalFeedback.guests?.room_no || '—'}</span>
                  </div>
                  <div className="flex items-center gap-2 pt-0.5">
                    {renderStars(responseModalFeedback.overall_rating)}
                    {responseModalFeedback.follow_up_required && (
                      <span className="text-rose-600 font-semibold">• Follow-up Required</span>
                    )}
                  </div>
                  {responseModalFeedback.comment && (
                    <p className="text-gray-600 italic pt-1 line-clamp-2">
                      &ldquo;{responseModalFeedback.comment}&rdquo;
                    </p>
                  )}
                </div>

                {/* Previously Logged Reviews (if any) */}
                {responseModalFeedback.feedback_responses && responseModalFeedback.feedback_responses.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-gray-700">
                      Previously Logged Reviews ({responseModalFeedback.feedback_responses.length})
                    </p>
                    <div className="max-h-36 overflow-y-auto space-y-1.5 p-2 bg-slate-50 rounded-lg border border-slate-200">
                      {responseModalFeedback.feedback_responses.map((resp, pIdx) => {
                        const parsed = parseResponseContent(resp.response_text, resp.dashboard_users);
                        return (
                          <div key={resp.id || pIdx} className="bg-white p-2 rounded-md border border-slate-200/80 text-xs space-y-1">
                            <div className="flex items-center justify-between text-[11px] text-gray-500">
                              <span className="font-semibold text-gray-700">
                                #{pIdx + 1} By <span className="text-indigo-600 font-bold">{parsed?.work_done_by || 'Staff'}</span>
                              </span>
                              <span className="text-[10px] text-gray-400">
                                {formatDateTimeIST(resp.created_at)}
                              </span>
                            </div>
                            <p className="text-gray-800 text-xs whitespace-pre-wrap">
                              {parsed?.remarks || resp.response_text}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Field 1: Work Done By */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Work Done By / Handled By <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Priya Sharma (Guest Relations)"
                    value={responseForm.work_done_by}
                    onChange={(e) => setResponseForm((prev) => ({ ...prev, work_done_by: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                  />
                  <p className="text-[11px] text-gray-400 mt-1">Name of the staff member who contacted the guest or handled the issue.</p>
                </div>

                {/* Field 2: Remarks / Action Taken */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Action Taken &amp; Remarks <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    rows={4}
                    placeholder="e.g. Called guest directly regarding the room service delay. Apologized and offered complimentary dining voucher for next stay. Guest was delighted with the prompt follow-up."
                    value={responseForm.remarks}
                    onChange={(e) => setResponseForm((prev) => ({ ...prev, remarks: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                  />
                </div>

                {/* Field 3: Status Selector */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Update Status
                  </label>
                  <select
                    value={responseForm.update_status}
                    onChange={(e) => setResponseForm((prev) => ({ ...prev, update_status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white"
                  >
                    <option value="resolved">Mark as Resolved</option>
                    <option value="acknowledged">Mark as Acknowledged (In Progress)</option>
                    <option value="submitted">Keep as Submitted</option>
                  </select>
                </div>

                {/* Footer Buttons */}
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setResponseModalFeedback(null)}
                    className="px-3.5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-xs font-semibold text-gray-700 transition cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-xs font-bold shadow-xs disabled:opacity-50 transition cursor-pointer"
                  >
                    {submitting ? 'Saving...' : (responseModalFeedback.feedback_responses?.length > 0 ? 'Add Review' : 'Save Remarks')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default FeedbackDashboard;
